import { Col, Form, message, Radio, Row } from "antd";
import { omit } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { match, P } from "ts-pattern";
import { AppDispatch } from "../../../../../redux/store";
import { Booking, TenantType, UsageType } from "../../../../../types/bookings";
import { InputRadio } from "../../../../atoms/FormElement";
import { PopupConfirm } from "../../../../atoms/Popup";
import { getValidationProps } from "../../../../libs/form";
import { mergeKeepingCommon } from "../../../../libs/utils";
import { getTenantDetailByEmail } from "../../ducks/actions";
import { getKycDetailsService, getTenantDetailByEmailService, translateService } from "../../ducks/services";
import CommonCard from "../CommonCard";
import KycDetails from "../KycDetails";
import { filterDropdownValue, marital_status, radioOptions, validateEmail } from "../utils";
import { SignatoryFields } from "./SignatoryFields";
import { TenantDetailForm } from "./TenantDetailForm";
import { TenantTypeRadio } from "./TenantTypeRadio";
import { handleBooking, prepareFormData } from "./utils";

let timeOutId: NodeJS.Timeout;
let aTimeOutId: NodeJS.Timeout;

type TenantDetailsProps = {
  parentState: (obj: object) => void;
  usageType: UsageType;
};

export const TenantDetails = (props: TenantDetailsProps) => {
  const { parentState, usageType } = props;

  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { leaseId = "" } = useParams();

  const { configData, bookingForm, bookingDetails } = useSelector((state: any) => state.newBooking);

  const [{ kycDetails, isSignatory }, setState] = useState({
    kycDetails: [],
    isSignatory: bookingForm?.isSignatory || bookingDetails?.isSignatory || "yes",
  });
  const updateState = (state: {}) => setState((prevState: any) => ({ ...prevState, ...state }));

  const [tenantType, setTenantType] = useState<TenantType>(
    bookingDetails?.tenantDetails?.tenantIdentityType || bookingForm?.tenantDetails?.tenantIdentityType || "company"
  );

  const methods = useForm();

  const {
    trigger,
    control,
    setValue,
    getValues,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = methods;

  const watchedFields = watch();

  const isEditForm = useMemo(() => {
    return Boolean(bookingDetails && Object.keys(bookingDetails).length > 0 && bookingDetails?.tenantDetails?.email);
  }, [bookingDetails]);

  const showSignatoryFields = match<[string, string | null, string]>([usageType, tenantType, isSignatory])
    .with(["residential", P._, "no"], () => true)
    .with(["commercial", "company", P._], () => true)
    .with(["commercial", "individual", "no"], () => true)
    .otherwise(() => false);

  const { tenantEmail, tenantName, showSignatoryRadioButton } = useMemo(() => {
    return match<[UsageType, TenantType]>([usageType, tenantType])
      .with(["residential", P._], () => {
        const firstName = watchedFields?.firstName || "";
        const lastName = watchedFields?.lastName || "";
        const fullName = [firstName, lastName].filter(Boolean).join(" ");
        return {
          tenantEmail: watchedFields?.email,
          tenantName: fullName || watchedFields?.name,
          showSignatoryRadioButton: true,
        };
      })
      .with(["commercial", "company"], () => ({
        tenantEmail: watchedFields?.commercial?.company?.email,
        tenantName: watchedFields?.commercial?.company?.name,
        showSignatoryRadioButton: false,
      }))
      .with(["commercial", "individual"], () => ({
        tenantEmail: watchedFields?.commercial?.individual?.email,
        tenantName: watchedFields?.commercial?.individual?.name,
        showSignatoryRadioButton: true,
      }))
      .otherwise(() => null);
  }, [usageType, tenantType, watchedFields]);

  const signatoryEmail = useWatch({ control, name: "signatoryUser.email" });
  const signatoryName = useWatch({ control, name: "signatoryUser.name" });
  const previousSignatoryEmail = useRef("");
  const latestSignatoryLookupId = useRef(0);

  const normalizeSelectValue = (value) => {
    if (!value) return undefined;
    if (typeof value === "string") return value;
    return value?.value || value?.label;
  };

  const tenantFormValues = useMemo(() => {
    const hasBookingForm = bookingForm && Object.entries(bookingForm).length > 0;
    const hasBookingDetails = bookingDetails && Object.keys(bookingDetails).length > 0;
    if (!hasBookingForm && !hasBookingDetails) return { tenantType };

    const currentValues = getValues();

    const defaultFields = {
      tenantType,
      isSignatory,
      ...(tenantType === "company" || (tenantType === "individual" && isSignatory === "no")
        ? { signatoryUser: bookingForm.signatoryUser }
        : {}),
    };

    const { tenantDetails } = bookingDetails as Booking;

    const mappedFields = match<[string, string]>([usageType, tenantType])
      .with(["commercial", "company"], () => ({
        commercial: {
          company: {
            email: tenantDetails?.email || currentValues?.commercial?.company?.email || bookingForm?.email || "",
            name: tenantDetails?.tenantNameEN || currentValues?.commercial?.company?.name || bookingForm?.name || "",
            phone: tenantDetails?.companyPhone || currentValues?.commercial?.company?.phone || bookingForm?.mobile,
            address: tenantDetails?.address || currentValues?.commercial?.company?.address || bookingForm?.address,
            city: tenantDetails?.city || currentValues?.commercial?.company?.city || bookingForm?.city,
            ...(tenantDetails?.country || currentValues?.commercial?.company?.country || bookingForm?.country
              ? {
                  country: filterDropdownValue(
                    normalizeSelectValue(tenantDetails?.country) ||
                      currentValues?.commercial?.company?.country?.value ||
                      normalizeSelectValue(bookingForm?.country),
                    configData?.nationalityList
                  ),
                }
              : {}),
            poBox: tenantDetails?.poBox || currentValues?.commercial?.company?.poBox || bookingForm?.poBox,
            licenseNo:
              tenantDetails?.tradeLicense || currentValues?.commercial?.company?.licenseNo || bookingForm?.licenseNo,
          },
        },
      }))
      .with(["commercial", "individual"], () => ({
        commercial: {
          individual: {
            email: tenantDetails?.email || currentValues?.commercial?.individual?.email || bookingForm?.email || "",
            name: tenantDetails?.tenantNameEN || currentValues?.commercial?.individual?.name || bookingForm?.name || "",
            mobile: tenantDetails?.mobileNumber || currentValues?.commercial?.individual?.mobile || bookingForm?.mobile,
            designation:
              tenantDetails?.designation ||
              currentValues?.commercial?.individual?.designation ||
              bookingForm?.designation,
            company: tenantDetails?.company || currentValues?.commercial?.individual?.company || bookingForm?.company,
          },
        },
      }))
      .with(["residential", P._], () => {
        // Get firstName and lastName - prioritize form values, then API data, then split old name
        const firstName = 
          currentValues?.firstName || 
          bookingForm?.firstName || 
          tenantDetails?.firstName || 
          "";
        const lastName = 
          currentValues?.lastName || 
          bookingForm?.lastName || 
          tenantDetails?.lastName || 
          "";
        
        // If firstName/lastName exist, use them; otherwise fallback to splitting old name
        let finalFirstName = firstName;
        let finalLastName = lastName;
        
        if (!finalFirstName && !finalLastName) {
          // Fallback: split old name for backward compatibility
          const tenantNameEN = tenantDetails?.tenantNameEN || currentValues?.name || bookingForm?.name || "";
          const nameParts = tenantNameEN.trim().split(/\s+/);
          finalFirstName = nameParts[0] || "";
          finalLastName = nameParts.slice(1).join(" ") || "";
        }
        
        // Always generate name from firstName and lastName (never use old name directly)
        const generatedName = [finalFirstName, finalLastName].filter(Boolean).join(" ") || "";
        
        return {
          email: tenantDetails?.email || currentValues?.email || bookingForm?.email || "",
          firstName: finalFirstName,
          lastName: finalLastName,
          name: generatedName,
          arabicName: tenantDetails?.tenantNameAR || currentValues?.arabicName || bookingForm?.arabicName,
          mobile: tenantDetails?.mobileNumber || currentValues?.mobile || bookingForm?.mobile,
          // Prefer API/bookingForm values over any existing form values (prevents default "971" overriding API)
          countryCode: bookingForm?.countryCode || tenantDetails?.countryCode || currentValues?.countryCode || "971",
          workPhone: currentValues?.workPhone || bookingForm?.workPhone || tenantDetails?.workPhone || "",
          nationality: filterDropdownValue(
            tenantDetails?.nationality || currentValues?.nationality?.value || bookingForm?.nationality,
            configData?.nationalityList
          ),
          maritalStatus: filterDropdownValue(
            tenantDetails?.maritalStatus || currentValues?.maritalStatus?.value || bookingForm?.maritalStatus,
            marital_status
          ),
        };
      })
      .otherwise(() => defaultFields);

    return { ...defaultFields, ...mappedFields };
  }, [bookingForm, bookingDetails, tenantType, isSignatory, usageType, configData, getValues]);

  useEffect(() => {
    const shouldPrefill = (bookingForm && Object.keys(bookingForm).length > 0) || isEditForm;
    if (!shouldPrefill) return;

    const values = getValues();
    const nextValues = tenantFormValues as Record<string, any>;

    const result: Record<string, any> = mergeKeepingCommon(values, nextValues) as Record<string, any>;
    
    // Ensure API values override existing form values for countryCode and workPhone
    // (mergeKeepingCommon might preserve existing values when both are non-empty)
    // Only apply for residential usage type where these fields exist
    if (usageType === "residential") {
      if (nextValues.countryCode) {
        result.countryCode = nextValues.countryCode;
      }
      if (nextValues.workPhone !== undefined) {
        result.workPhone = nextValues.workPhone;
      }
    }
    
    reset(result, { keepDefaultValues: false });
  }, [bookingForm, isEditForm, tenantFormValues, getValues, reset]);

  const isBooking = bookingDetails?.kycDetails && Object.keys(omit(bookingDetails, "kycDetails")).length > 0;

  const checkUserIsBlocked = () => {
    PopupConfirm({
      title: `This tenant is blacklisted and cannot be added to a new booking.`,
      onOk: async () => {
        try {
          reset(null, { keepDefaultValues: false });
          await dispatch(getTenantDetailByEmail(tenantEmail, true));
        } catch ({ response }) {
          message.error(response?.data?.error);
        }
      },
      okText: "Ok",
    });
  };

  useEffect(() => {
    if (isBooking) {
      updateState({
        isSignatory: bookingForm?.isSignatory || bookingDetails?.isSignatory,
      });
      setValue("isSignatory", bookingForm?.isSignatory || bookingDetails?.isSignatory);
    }

    if (bookingForm?.isBlocked) {
      checkUserIsBlocked();
    }
    getKycDetails();
  }, [isBooking, bookingForm?.isBlocked]);

  useEffect(() => {
    if (validateEmail(tenantEmail) && tenantEmail !== bookingForm?.email) {
      dispatch(getTenantDetailByEmail(tenantEmail, false));
    }
  }, [tenantEmail, bookingForm?.email, dispatch]);

  useEffect(() => {
    const normalizedSignatoryEmail = signatoryEmail?.trim()?.toLowerCase() || "";

    if (!normalizedSignatoryEmail || normalizedSignatoryEmail === previousSignatoryEmail.current) return;

    previousSignatoryEmail.current = normalizedSignatoryEmail;

    if (!validateEmail(normalizedSignatoryEmail)) return;

    getSignatoryTenant(normalizedSignatoryEmail);
  }, [signatoryEmail]);

  useEffect(() => {
    if (tenantName && tenantName.length > 1) {
      clearTimeout(timeOutId);

      timeOutId = setTimeout(async () => {
        const response = await translateService(tenantName);
        if (response?.translatedText) setValue("arabicName", response?.translatedText);
      }, 2000);
    }

    if (signatoryName && signatoryName.length > 1) {
      clearTimeout(aTimeOutId);

      aTimeOutId = setTimeout(async () => {
        const response = await translateService(signatoryName);
        if (response?.translatedText) setValue("signatoryUser.arabicName", response?.translatedText);
      }, 2000);
    }
  }, [tenantName, signatoryName]);

  // Update name field from firstName and lastName for residential tenants
  useEffect(() => {
    if (usageType === "residential") {
      const firstName = watchedFields?.firstName || "";
      const lastName = watchedFields?.lastName || "";
      const fullName = [firstName, lastName].filter(Boolean).join(" ");
      
      if (fullName) {
        setValue("name", fullName, { shouldValidate: false });
      }
    }
  }, [watchedFields?.firstName, watchedFields?.lastName, usageType, setValue]);

  const getSignatoryTenant = async (email: string) => {
    const lookupId = latestSignatoryLookupId.current + 1;
    latestSignatoryLookupId.current = lookupId;

    try {
      const response = await getTenantDetailByEmailService(email);
      if (lookupId !== latestSignatoryLookupId.current) return;

      if (response) {
        setValue("signatoryUser.email", response?.email);
        setValue("signatoryUser.name", response?.tenantNameEN);
        setValue("signatoryUser.arabicName", response?.tenantNameAR);
        setValue("signatoryUser.mobile", response?.mobileNumber);
        setValue("signatoryUser.designation", response?.designationAsPerVisa || response?.designation);
        setValue("signatoryUser.company", response?.company);
      }
    } catch (error: any) {}
  };

  const getKycDetails = async () => {
    try {
      const unitId = !isBooking ? state?.selectedUnit?.unitId : bookingDetails?.unitId;

      const response = await getKycDetailsService(unitId);
      if (response?.length > 0) updateState({ kycDetails: response });
    } catch (error) {}
  };

  const onSubmit: SubmitHandler<Record<string, unknown>> = async (data) => {
    const formData = prepareFormData({
      rawData: data || getValues(),
      usageType,
      isBooking,
      unitCode: state?.selectedUnit.unit.unitCode ?? bookingDetails?.propertyDetails?.unitCode,
      kycDetails,
    });

    await handleBooking({
      formData,
      isBooking,
      leaseId,
      usageType,
      dispatch,
      parentState,
      navigate,
    });
  };

  const handleNext = async () => {
    const valid = await trigger();
    if (!valid) return;

    const data = getValues();
    await onSubmit(data);
  };

  const handleBack = () => navigate("/bookings");

  const handleTenantTypeChange = (newTenantType: TenantType) => {
    reset({ tenantType: newTenantType, isSignatory: "yes", signatoryUser: {} }, { keepDefaultValues: false });

    setTenantType(newTenantType);
    updateState({ isSignatory: "yes" });
  };

  return (
    <FormProvider {...methods}>
      <Form
        layout="vertical"
        name="bookingform"
        scrollToFirstError
        className="bf-main"
        onFinish={handleSubmit(onSubmit)}
      >
        <CommonCard
          leftBtnLabel="Back"
          handleBack={handleBack}
          rightBtnLabel="Next"
          handleNext={handleNext}
          label="Tenant Details"
          parentState={parentState}
          isBlocked={bookingForm?.isBlocked}
        >
          <Col span={24}>
            {usageType === "commercial" && (
              <TenantTypeRadio tenantType={tenantType} onChange={handleTenantTypeChange} isDisabled={isEditForm} />
            )}

            <TenantDetailForm usageType={usageType} tenantType={tenantType} />

            <Row gutter={24}>
              <KycDetails
                // key={errors}
                userKycInfo={bookingDetails?.kycDetails}
                control={control}
                colSpan={8}
                errors={Object?.keys(errors)?.length > 0 && errors}
                kycDetail={kycDetails}
                setValue={setValue}
              />
            </Row>

            {showSignatoryRadioButton && (
              <Row gutter={24}>
                <Col span={8}>
                  <InputRadio
                    initValue="yes"
                    fieldname="isSignatory"
                    label="Is the tenant the authorized signatory?"
                    control={control}
                    setValue={setValue}
                    {...getValidationProps({ fieldName: "isSignatory", errors, isRequired: true })}
                    onChange={(e: any) => updateState({ isSignatory: e.target.value })}
                    options={
                      <Row gutter={[16, 16]}>
                        {radioOptions.map((option, i) => (
                          <Col span={6} key={`${option.value}-${i}`}>
                            <Radio value={option.value}>{option.label}</Radio>
                          </Col>
                        ))}
                      </Row>
                    }
                  />
                </Col>
              </Row>
            )}

            {showSignatoryFields && <SignatoryFields usageType={usageType} />}
          </Col>
        </CommonCard>
      </Form>
    </FormProvider>
  );
};
